import { ConditionViewerItem, ConditionViewerTerm } from '@customerio/pluma-components/react';
import type { MessageNode } from '../../types';
import { getIconForNode } from '../data-source-icon';
import { Times, Within } from '../condition-parts';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';

function useCampaignName(
  campaignId: number | undefined,
  fallbackName: string | undefined,
  workspaceId: number,
): string | undefined {
  const shouldFetch = campaignId != null && campaignId > 0 && fallbackName == null && workspaceId > 0;

  const { data } = useHydraUIAPIQuery({
    queryKey: ['getCampaignById', { params: { environment_id: workspaceId, campaign_id: String(campaignId ?? 0) } }],
    queryFn: ({ signal }) =>
      hydraUiApi.campaigns.get({
        params: { path: { environment_id: String(workspaceId), campaign_id: String(campaignId ?? 0) } },
        signal,
      }),
    queryConfig: { enabled: shouldFetch, staleTime: 5 * 60 * 1000 },
  });

  if (fallbackName != null) return fallbackName;
  return data?.campaign?.name;
}

interface MessageConditionProps {
  node: MessageNode;
  workspaceId: number;
}

export function MessageCondition({ node, workspaceId }: MessageConditionProps) {
  const campaignName = useCampaignName(node.campaignId, node.campaignName, workspaceId);

  return (
    <ConditionViewerItem icon={getIconForNode(node)}>
      {node.displayName === '' ? null : <ConditionViewerTerm>{node.displayName}</ConditionViewerTerm>}
      {campaignName != null ? (
        <>
          {node.displayName === '' ? null : ' '}
          in <ConditionViewerTerm>{campaignName}</ConditionViewerTerm>
        </>
      ) : null}{' '}
      {node.action}
      {node.humanOnly ? ' by a human' : null}
      {node.href != null ? (
        <>
          {' '}
          on the tracked link {node.hrefOperator == null ? 'matching' : `that ${node.hrefOperator}`} "{node.href}"
        </>
      ) : null}
      <Times times={node.times} />
      <Within within={node.within} />
    </ConditionViewerItem>
  );
}
